import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, gbp, fmtDate, fileUrl, downloadPdf, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search, Download, Trash2, Pencil, BookOpen, Mail } from "lucide-react";
import { toast } from "sonner";

export default function SelfBills() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [emailFor, setEmailFor] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/self-bills").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = items.filter((it) => {
    const s = q.toLowerCase();
    return (
      !s ||
      it.number.toLowerCase().includes(s) ||
      (it.driver_snapshot?.name || "").toLowerCase().includes(s) ||
      (it.driver_snapshot?.driver_id || "").toLowerCase().includes(s)
    );
  });

  const onDelete = async (sb) => {
    if (!window.confirm(`Delete ${sb.number}?`)) return;
    try {
      await api.delete(`/self-bills/${sb.id}`);
      toast.success(`${sb.number} deleted`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onDownload = async (sb) => {
    try { await downloadPdf(`/self-bills/${sb.id}/pdf`, `${sb.number}.pdf`); }
    catch { toast.error("Could not download PDF"); }
  };

  return (
    <div data-testid="sb-list-page" className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Documents</div>
          <h1 className="font-display text-3xl tracking-tight font-semibold text-slate-900 mt-1">Self-Bills</h1>
          <p className="text-slate-600 mt-2 text-sm">Issue self-billed invoices to your self-employed drivers.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/self-bills/register">
            <Button data-testid="sb-register-btn" variant="outline" className="rounded-sm border-slate-300">
              <BookOpen className="h-4 w-4 mr-2" /> View Register
            </Button>
          </Link>
          <Link to="/self-bills/new">
            <Button data-testid="sb-new-btn" className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white">
              <Plus className="h-4 w-4 mr-2" /> New Self-Bill
            </Button>
          </Link>
        </div>
      </div>

      <Card className="rounded-sm border border-slate-200 shadow-none">
        <div className="p-4 border-b border-slate-200 flex items-center gap-3">
          <div className="relative w-full max-w-sm">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="sb-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by reference, driver name or ID…"
              className="pl-9 rounded-sm border-slate-300"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full po-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider">
                <th className="px-5 py-3 font-semibold">Reference</th>
                <th className="px-5 py-3 font-semibold">Driver</th>
                <th className="px-5 py-3 font-semibold">Pay period</th>
                <th className="px-5 py-3 font-semibold">Due</th>
                <th className="px-5 py-3 font-semibold text-right">Total</th>
                <th className="px-5 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="text-sm text-slate-900">
              {loading && (
                <tr><td colSpan="6" className="px-5 py-10 text-center text-slate-500">Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan="6" className="px-5 py-12 text-center">
                  <div className="text-slate-700 font-medium">No self-bills yet</div>
                  <div className="text-slate-500 text-sm mt-1">Create your first self-bill to get started.</div>
                </td></tr>
              )}
              {!loading && filtered.map((sb) => (
                <tr key={sb.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`sb-row-${sb.id}`}>
                  <td className="px-5 py-3 font-medium">
                    <Link to={`/self-bills/${sb.id}`} className="hover:underline">{sb.number}</Link>
                  </td>
                  <td className="px-5 py-3 text-slate-700">
                    {sb.driver_snapshot?.name || "—"}
                    {sb.driver_snapshot?.driver_id && (
                      <span className="text-slate-400 text-xs ml-2">{sb.driver_snapshot.driver_id}</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-slate-700">{fmtDate(sb.pay_period_from)} – {fmtDate(sb.pay_period_to)}</td>
                  <td className="px-5 py-3 text-slate-700">{fmtDate(sb.due_date)}</td>
                  <td className="px-5 py-3 text-right tabular-nums font-medium">{gbp(sb.total)}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <Button data-testid={`sb-pdf-${sb.id}`} variant="ghost" size="icon" className="h-8 w-8 rounded-sm" onClick={() => onDownload(sb)} title="Download PDF">
                        <Download className="h-4 w-4" />
                      </Button>
                      <Link to={`/self-bills/${sb.id}`}>
                        <Button data-testid={`sb-edit-${sb.id}`} variant="ghost" size="icon" className="h-8 w-8 rounded-sm" title="View / edit">
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </Link>
                      <Button data-testid={`sb-delete-${sb.id}`} variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-red-600 hover:text-red-700" onClick={() => onDelete(sb)} title="Delete">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
